import { store } from 'createStore';
import type * as go from 'gojs';
import type { AvailablePort } from 'gojs/types';
import { PortMode } from 'store/units/types';
import {
  CONVERSIONS,
  convertToCurrentMetric,
  getMetricUnit,
} from 'util/conversions';

const isAvailablePort = (value: unknown): value is AvailablePort => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'key' in value &&
    'connected' in value
  );
};

export const getPortData = (
  node: go.Node,
  port: go.GraphObject,
): AvailablePort | null => {
  const nodeData = node.data as Record<string, unknown>;
  const portId = port.portId;
  const portLists = [
    nodeData.inputPortArray,
    nodeData.outputPortArray,
    nodeData.D0,
    nodeData.D1,
    nodeData.D2,
    nodeData.D3,
    nodeData.D4,
    nodeData.D5,
    nodeData.P0,
    nodeData.P1,
    nodeData.P2,
    nodeData.P3,
    nodeData.P4,
    nodeData.P5,
  ];

  for (const portList of portLists) {
    if (!Array.isArray(portList)) {
      continue;
    }

    const portData = portList.find(
      (candidate) => isAvailablePort(candidate) && candidate.key === portId,
    );

    if (isAvailablePort(portData)) {
      return portData;
    }
  }

  return null;
};

const isOutputPortConnected = (portKey: string) =>
  store
    .getState()
    .unitConnGroup.present.connectionState.connections.some(
      (connection) => connection.fromPort === portKey,
    );

const isEmptyOutputPort = (
  port: AvailablePort | null | undefined,
): port is AvailablePort => {
  if (port?.portMode !== PortMode.OUTPUT) {
    return false;
  }

  return !isOutputPortConnected(port.key);
};

const getActiveOutputPortLimit = (nodeData: Record<string, unknown>) => {
  if (typeof nodeData.nbrOfDecks === 'number') {
    return nodeData.nbrOfDecks + 1;
  }

  if (typeof nodeData.nbrOfOutputs === 'number') {
    return nodeData.nbrOfOutputs;
  }

  if (typeof nodeData.numberOfPorts === 'number') {
    return nodeData.numberOfPorts;
  }

  const layout = nodeData.layout;

  if (
    typeof layout === 'object' &&
    layout !== null &&
    'outputPorts' in layout &&
    typeof layout.outputPorts === 'number'
  ) {
    return layout.outputPorts;
  }

  return null;
};

const isActiveOutputPort = (
  port: AvailablePort,
  nodeData: Record<string, unknown>,
) => {
  const activeOutputPortLimit = getActiveOutputPortLimit(nodeData);

  if (activeOutputPortLimit === null) {
    return true;
  }

  const normalizedPortIndex = port.portIndex === 0 ? 1 : port.portIndex;

  return normalizedPortIndex <= activeOutputPortLimit;
};

export const isSmartSelectableOutputPort = (
  node: go.Node,
  port: AvailablePort | null | undefined,
): port is AvailablePort => {
  if (!port) {
    return false;
  }

  return (
    isActiveOutputPort(port, node.data as Record<string, unknown>) &&
    isEmptyOutputPort(port)
  );
};

export const getSmartSelectOutputPorts = (node: go.Node) => {
  const nodeData = node.data as Record<string, unknown>;
  const portLists = [
    nodeData.outputPortArray,
    nodeData.D0,
    nodeData.D1,
    nodeData.D2,
    nodeData.D3,
    nodeData.D4,
    nodeData.D5,
    nodeData.P0,
    nodeData.P1,
    nodeData.P2,
    nodeData.P3,
    nodeData.P4,
    nodeData.P5,
  ];
  const portsByKey = new Map<string, AvailablePort>();

  for (const portList of portLists) {
    if (!Array.isArray(portList)) {
      continue;
    }

    portList.forEach((port) => {
      if (isAvailablePort(port) && isSmartSelectableOutputPort(node, port)) {
        portsByKey.set(port.key, port);
      }
    });
  }

  return Array.from(portsByKey.values()).sort((first, second) => {
    if (first.portIndex !== second.portIndex) {
      return first.portIndex - second.portIndex;
    }

    return first.key.localeCompare(second.key);
  });
};

export const getSmartSelectPortLabel = (port: AvailablePort) =>
  `Output ${port.portIndex === 0 ? 1 : port.portIndex}`;

export const getSmartSelectParticleSize = (port: AvailablePort) => {
  const topSize = port.outputFlow?.topSize;

  if (typeof topSize !== 'number' || !Number.isFinite(topSize)) {
    return 'Any';
  }

  const metricType = store.getState().settingsState.metricType;
  const size = convertToCurrentMetric({
    value: topSize,
    metricType,
    conversionType: CONVERSIONS.MILLIMETER,
  });

  return `~ ${size} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`;
};

export const getSmartSelectCapacity = (port: AvailablePort) => {
  const totalMass = port.outputFlow?.totalMass;

  if (typeof totalMass !== 'number' || !Number.isFinite(totalMass)) {
    return 'Any';
  }

  const metricType = store.getState().settingsState.metricType;
  const capacity = convertToCurrentMetric({
    value: totalMass,
    metricType,
    conversionType: CONVERSIONS.CAPACITY,
  });

  return `~ ${capacity} ${getMetricUnit(metricType, CONVERSIONS.CAPACITY)}`;
};
